import React, {
  useEffect,
  useRef,
  useId,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import { db } from '../../services/supabase';

import AppLayout from '../../components/Layout/AppLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import InfoTooltip from '../../components/UI/InfoTooltip';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { useFamilyStatus } from '../../contexts/FamilyStatusContext';
import { formatCurrency } from '../../utils/currency';
import '../../styles/brand-typography.css';
import './ManageChores.css';


// ======================================================
// Helpers
// ======================================================

const toCents = (val) => {
  const cleaned = String(
    val ?? ''
  ).replace(
    /[^0-9.]/g,
    ''
  );

  const num = Number(cleaned);

  if (!Number.isFinite(num)) {
    return 0;
  }

  return Math.round(
    num * 100
  );
};


const centsToDollars = (
  cents
) => {
  return (
    Number(cents || 0) / 100
  ).toFixed(2);
};


// Native dialog provides keyboard focus containment, Escape handling and focus return.
const ChoreDialog = ({ title, busy, onClose, children }) => {
  const dialog = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);
  return (
    <dialog ref={dialog} aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
      className="chore-dialog m-auto w-[calc(100%-2rem)] max-w-lg rounded-2xl border border-gray-200 bg-white p-5 text-gray-900 shadow-2xl dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 id={titleId} className="flex items-center gap-2 text-xl font-extrabold"><MyPiggyIcon name="do-chores" size="sm" />{title}</h2>
        <button type="button" onClick={onClose} disabled={busy} aria-label={`Close ${title}`} className="h-10 w-10 shrink-0 rounded-full bg-gray-100 text-xl hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-50 dark:bg-slate-700 dark:hover:bg-slate-600">×</button>
      </div>
      {children}
    </dialog>
  );
};

const ChoreFields = ({ value, onChange, childrenList, prefix }) => (
  <div className="space-y-4">
    <Input label="Chore Title" name="title" value={value.title} onChange={(e) => onChange((previous) => ({ ...previous, title: e.target.value }))} placeholder="Clean your room" required minLength={2} />
    <Input label="Reward ($)" name="amount" inputMode="decimal" value={value.amount} onChange={(e) => onChange((previous) => ({ ...previous, amount: e.target.value }))} placeholder="5.00" required />
    <div>
      <div className="mb-2 flex items-center gap-2">
        <label htmlFor={`${prefix}-assignment`} className="text-sm font-bold">Who Can Do This Chore?</label>
        <InfoTooltip compact label="About chore assignment">All Kids makes this a Family Chore available to every active child. Choose a child to assign it only to them.</InfoTooltip>
      </div>
      <select id={`${prefix}-assignment`} value={value.assigned_child_id} onChange={(e) => onChange((previous) => ({ ...previous, assigned_child_id: e.target.value }))}>
        <option value="">All Kids (Family Chore)</option>
        {value.assigned_child_id && !childrenList.some((child) => child.id === value.assigned_child_id) && <option value={value.assigned_child_id}>Previously assigned child</option>}
        {childrenList.map((child) => <option key={child.id} value={child.id}>{child.nickname || child.full_name}</option>)}
      </select>
    </div>
  </div>
);

const ChoreActions = ({ chore, disabled, onEdit, onToggle }) => {
  const [open, setOpen] = useState(false);
  const container = useRef(null);
  const popupId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => { if (!container.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === 'Escape') { setOpen(false); container.current?.querySelector('[aria-expanded]')?.focus(); } };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', closeOutside); document.removeEventListener('keydown', escape); };
  }, [open]);
  return (
    <div ref={container} className="relative flex items-center justify-end gap-1">
      <Button variant="secondary" size="sm" disabled={disabled} onClick={onEdit} aria-label={`Edit ${chore.title}`}>Edit</Button>
      <button type="button" disabled={disabled} aria-label={`More actions for ${chore.title}`} aria-expanded={open && !disabled} aria-controls={popupId} onClick={() => setOpen((value) => !value)} className="h-10 w-10 rounded-lg text-xl font-bold hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-40 dark:hover:bg-slate-700">⋯</button>
      {open && !disabled && <div id={popupId} className="absolute right-0 top-full z-20 mt-1 min-w-40 rounded-xl border border-gray-200 bg-white p-1 shadow-xl dark:border-slate-600 dark:bg-slate-800">
        <button type="button" onClick={() => { setOpen(false); onToggle(); }} className={`w-full rounded-lg px-3 py-3 text-left text-sm font-bold focus-visible:ring-2 focus-visible:ring-teal-500 ${chore.is_active ? 'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/50' : 'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/50'}`}>{chore.is_active ? 'Deactivate' : 'Reactivate'}</button>
      </div>}
    </div>
  );
};

// ======================================================
// ManageChores
// ======================================================
//
// Parent chore catalog.
//
// Supports:
//
// - Create chore
// - Assign to all kids
// - Assign to one kid
// - Edit chore
// - Deactivate chore
// - Reactivate chore
// - Show active/inactive chores
// - Identify starter chores
// - Identify parent-created chores
// - Identify kid-created chores
//
// We intentionally DEACTIVATE instead of DELETE so
// historical chore claims and transactions remain safe.
// ======================================================

const ManageChores = () => {
  const navigate =
    useNavigate();

  const { parent } =
    useAuth();

  const {
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useFamilyStatus();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;

  // --------------------------------------------------
  // Data
  // --------------------------------------------------

  const [
    children,
    setChildren,
  ] = useState([]);


  const [
    chores,
    setChores,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    saving,
    setSaving,
  ] = useState(false);


  const [
    actionChoreId,
    setActionChoreId,
  ] = useState(null);


  const [
    error,
    setError,
  ] = useState('');


  const [
    message,
    setMessage,
  ] = useState('');


  // --------------------------------------------------
  // Filters
  // --------------------------------------------------

  const [
    statusFilter,
    setStatusFilter,
  ] = useState('active');


  const [
    sourceFilter,
    setSourceFilter,
  ] = useState('all');


  const [
    childFilter,
    setChildFilter,
  ] = useState('all');


  // --------------------------------------------------
  // Create form
  // --------------------------------------------------

  const [
    form,
    setForm,
  ] = useState({
    title: '',
    amount: '',
    assigned_child_id: '',
  });


  // --------------------------------------------------
  // Edit state
  // --------------------------------------------------

  const [
    editingChoreId,
    setEditingChoreId,
  ] = useState(null);


  const [
    editForm,
    setEditForm,
  ] = useState({
    title: '',
    amount: '',
    assigned_child_id: '',
  });


  // --------------------------------------------------
  // Child lookup
  // --------------------------------------------------

  const childById =
    useMemo(() => {
      const map =
        new Map();

      (
        children || []
      ).forEach(
        (child) => {
          map.set(
            child.id,
            child
          );
        }
      );

      return map;
    }, [children]);


  // --------------------------------------------------
  // Load data
  // --------------------------------------------------

  const load = async ({
    showLoading = false,
  } = {}) => {

    if (!parent?.family_id) {
      return;
    }


    try {
      if (showLoading) {
        setLoading(true);
      }

      setError('');


      const [
        kids,
        choresData,
        pending,
      ] = await Promise.all([
        db.getActiveChildren(
          parent.family_id
        ),

      db.getChores(
        parent.family_id,
        {
          includeInactive: true,
        }
      ),
        db.getChoreClaims(parent.family_id, 'pending').then((rows) => (rows || []).length).catch(() => null),
      ]);


      setPendingCount(pending);
      setChildren(
        kids || []
      );


      setChores(
        choresData || []
      );
    } catch (err) {
      console.error(
        'ManageChores load error:',
        err
      );

      setError(
        err?.message ||
          'Unable to load chores.'
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };


  // --------------------------------------------------
  // Initial load
  // --------------------------------------------------

  useEffect(() => {
    if (parent?.family_id) {
      load({
        showLoading: true,
      });
    }
  }, [parent?.family_id]);


  // --------------------------------------------------
  // Create chore
  // --------------------------------------------------

  const handleCreate =
    async (e) => {

      e.preventDefault();
      if (isWriteLocked || saving || actionChoreId) {
        return;
      }


      if (!parent?.family_id) {
        return;
      }


      try {
        setError('');
        setMessage('');
        setSaving(true);


        const title =
          form.title.trim();


        const value_cents =
          toCents(
            form.amount
          );


        if (
          !title ||
          title.length < 2
        ) {
          throw new Error(
            'Chore title must be at least 2 characters.'
          );
        }


        if (
          value_cents <= 0
        ) {
          throw new Error(
            'Chore value must be greater than $0.'
          );
        }


        const assigned_child_id =
          form.assigned_child_id
            ? form.assigned_child_id
            : null;


        await db.createChore({
          family_id:
            parent.family_id,

          title,

          value_cents,

          is_active: true,

          created_by:
            'parent',

          created_by_child_id:
            null,

          assigned_child_id,

          source:
            'parent',
        });


        setForm({
          title: '',
          amount: '',
          assigned_child_id: '',
        });


        setMessage(
          `✅ "${title}" was added.`
        );
        setShowCreateForm(false);


        await load();
      } catch (err) {
        console.error(
          'ManageChores create error:',
          err
        );

        setError(
          err?.message ||
            'Unable to create chore.'
        );
      } finally {
        setSaving(false);
      }
    };


  // --------------------------------------------------
  // Begin editing
  // --------------------------------------------------

  const startEditing =



  (chore) => {
      if (isWriteLocked || saving || actionChoreId) {
        return;
      } 

      setError('');
      setMessage('');


      setEditingChoreId(
        chore.id
      );


      setEditForm({
        title:
          chore.title || '',

        amount:
          centsToDollars(
            chore.value_cents
          ),

        assigned_child_id:
          chore.assigned_child_id ||
          '',
      });
    };


  // --------------------------------------------------
  // Cancel editing
  // --------------------------------------------------

  const cancelEditing =
    () => {

      setEditingChoreId(
        null
      );


      setEditForm({
        title: '',
        amount: '',
        assigned_child_id: '',
      });
    };


  // --------------------------------------------------
  // Save edited chore
  // --------------------------------------------------

  const saveEdit =


    async (chore) => {
      if (isWriteLocked || saving || actionChoreId) {
        return;
      }

      if (!chore?.id) {
        return;
      }


      try {
        setError('');
        setMessage('');

        setActionChoreId(
          chore.id
        );


        const title =
          editForm.title.trim();


        const value_cents =
          toCents(
            editForm.amount
          );


        if (
          !title ||
          title.length < 2
        ) {
          throw new Error(
            'Chore title must be at least 2 characters.'
          );
        }


        if (
          value_cents <= 0
        ) {
          throw new Error(
            'Chore value must be greater than $0.'
          );
        }


        const assigned_child_id =
          editForm.assigned_child_id
            ? editForm.assigned_child_id
            : null;


        await db.updateChore(
          chore.id,
          {
            title,
            value_cents,
            assigned_child_id,
          }
        );


        setMessage(
          `✅ "${title}" was updated.`
        );


        cancelEditing();

        await load();
      } catch (err) {
        console.error(
          'ManageChores edit error:',
          err
        );

        setError(
          err?.message ||
            'Unable to update chore.'
        );
      } finally {
        setActionChoreId(
          null
        );
      }
    };


  // --------------------------------------------------
  // Deactivate chore
  // --------------------------------------------------

  const deactivate =
    

    async (chore) => {
      if (isWriteLocked || saving || actionChoreId) {
        return;
      }

      if (!chore?.id) {
        return;
      }


      if (!window.confirm(`Deactivate "${chore.title}"?\n\nIt will no longer be available to kids. Completed chores and money history are kept.`)) return;

      try {
        setError('');
        setMessage('');

        setActionChoreId(
          chore.id
        );


        await db.updateChore(
          chore.id,
          {
            is_active: false,
          }
        );


        setMessage(
          `✅ "${chore.title}" was deactivated.`
        );


        if (
          editingChoreId ===
          chore.id
        ) {
          cancelEditing();
        }


        await load();
      } catch (err) {
        console.error(
          'ManageChores deactivate error:',
          err
        );

        setError(
          err?.message ||
            'Unable to deactivate chore.'
        );
      } finally {
        setActionChoreId(
          null
        );
      }
    };


  // --------------------------------------------------
  // Reactivate chore
  // --------------------------------------------------

  const reactivate =
    

    async (chore) => {
      if (isWriteLocked || saving || actionChoreId) {
        return;
      }

      if (!chore?.id) {
        return;
      }


      if (!window.confirm(`Reactivate "${chore.title}"?\n\nIt will be available to the assigned child or all active kids again.`)) return;

      try {
        setError('');
        setMessage('');

        setActionChoreId(
          chore.id
        );


        await db.updateChore(
          chore.id,
          {
            is_active: true,
          }
        );


        setMessage(
          `✅ "${chore.title}" is active again.`
        );


        await load();
      } catch (err) {
        console.error(
          'ManageChores reactivate error:',
          err
        );

        setError(
          err?.message ||
            'Unable to reactivate chore.'
        );
      } finally {
        setActionChoreId(
          null
        );
      }
    };


  // --------------------------------------------------
  // Source helpers
  // --------------------------------------------------

  const getSource =
    (chore) => {

      // New rows should have source.
      if (chore?.source) {
        return chore.source;
      }


      // Fallback for historical data created before
      // the source column existed.
      if (
        chore?.created_by ===
        'child'
      ) {
        return 'child';
      }


      return 'parent';
    };


  const getSourceLabel =
    (chore) => {

      const source =
        getSource(chore);


      if (
        source ===
        'child'
      ) {
        return 'Kid-created';
      }


      // Starter chores are grouped with parent-created
      // chores in the parent experience.
      return 'Parent-created';
    };


  const getSourceClass =
    (chore) => {

      const source =
        getSource(chore);


      if (
        source ===
        'child'
      ) {
        return (
          'bg-purple-100 dark:bg-purple-950/60 ' +
          'text-purple-700 dark:text-purple-300'
        );
      }


      return (
        'bg-blue-100 dark:bg-blue-950/60 ' +
        'text-blue-700 dark:text-blue-300'
      );
    };


  // --------------------------------------------------
  // Filter chores
  // --------------------------------------------------

  const filteredChores =
    useMemo(() => {

      return (
        chores || []
      ).filter(
        (chore) => {
          const query = search.trim().toLowerCase();
          if (query && !String(chore.title || '').toLowerCase().includes(query)) return false;

          // ------------------------------------------
          // Status
          // ------------------------------------------

          if (
            statusFilter ===
              'active' &&
            !chore.is_active
          ) {
            return false;
          }


          if (
            statusFilter ===
              'inactive' &&
            chore.is_active
          ) {
            return false;
          }


          // ------------------------------------------
          // Chore source
          // ------------------------------------------

          if (
            sourceFilter ===
            'parent'
          ) {
            const source =
              getSource(chore);

            if (
              source ===
              'child'
            ) {
              return false;
            }
          }


          if (
            sourceFilter ===
              'child' &&
            getSource(chore) !==
              'child'
          ) {
            return false;
          }


          // ------------------------------------------
          // Child
          // ------------------------------------------

          if (
            childFilter !==
            'all'
          ) {

            // Specific child:
            //
            // Show chores assigned directly to that
            // child PLUS family-wide chores that the
            // child can see.
            return (
              !chore.assigned_child_id ||
              chore.assigned_child_id ===
                childFilter
            );
          }


          return true;
        }
      );
    }, [
      chores,
      statusFilter,
      sourceFilter,
      childFilter,
      search,
    ]);


  // --------------------------------------------------
  // Counts
  // --------------------------------------------------

  const activeCount =
    useMemo(
      () =>
        (
          chores || []
        ).filter(
          (chore) =>
            chore.is_active
        ).length,

      [chores]
    );


  const inactiveCount =
    useMemo(
      () =>
        (
          chores || []
        ).filter(
          (chore) =>
            !chore.is_active
        ).length,

      [chores]
    );


  const resetFilters = () => { setSearch(''); setStatusFilter('active'); setSourceFilter('all'); setChildFilter('all'); };
  const filtersChanged = Boolean(search || statusFilter !== 'active' || sourceFilter !== 'all' || childFilter !== 'all');
  const selectedFilterCount = Number(statusFilter !== 'active') + Number(sourceFilter !== 'all') + Number(childFilter !== 'all');
  const editingChore = chores.find((chore) => chore.id === editingChoreId);
  const busy = saving || Boolean(actionChoreId);
  const closeCreate = () => { if (busy) return; setShowCreateForm(false); setForm({ title: '', amount: '', assigned_child_id: '' }); setError(''); };
  const closeEdit = () => { if (busy) return; cancelEditing(); setError(''); };

  return (
    <AppLayout title="Manage Chores" className="mypiggy-brand manage-chores" showBack onBack={() => navigate('/parent')}>
      {loading ? <p className="py-8 text-center">Loading chores…</p> : <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><h2 className="text-xl font-extrabold">Family Chores</h2><InfoTooltip label="About family chores">Manage parent-created and kid-created chores here. Selecting a child includes Family Chores they can do too.</InfoTooltip></div>
            <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">{activeCount} active · {inactiveCount} inactive</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate('/parent/chores/review')}>
              Review Completed Chores <span className="ml-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs font-extrabold text-teal-900 dark:bg-teal-900 dark:text-teal-100" aria-label={pendingCount === null ? 'Pending count unavailable' : `${pendingCount} completed chores waiting for approval`}>{pendingCount ?? '—'}</span>
            </Button>
              <InfoTooltip label="About chore approvals">These are completed chores from children who require parent approval. Approving a chore approves its linked reward transaction; rejecting it does not add the reward to Available Money.</InfoTooltip>
            </div>
            <Button disabled={isWriteLocked || busy} onClick={() => { setError(''); setMessage(''); setShowCreateForm(true); }}>+ Add Chore</Button>
          </div>
        </div>

        {message && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-200">{message}</p>}
        {error && !showCreateForm && !editingChore && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/35 dark:text-red-200">{error} <button type="button" onClick={() => load({ showLoading: true })} className="ml-2 font-bold underline">Retry loading</button></p>}

        <Card className="p-3">
          <div className="flex items-end gap-2">
            <Input id="chore-search" type="search" label="Search chores" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by chore title" className="min-w-0 flex-1" />
            <button type="button" aria-expanded={showFilters} aria-controls="chore-filters" onClick={() => setShowFilters((value) => !value)} className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm font-bold focus-visible:ring-2 focus-visible:ring-teal-500 dark:border-slate-600 md:hidden">Filters{selectedFilterCount ? ` (${selectedFilterCount})` : ''}</button>
          </div>
          <div id="chore-filters" className={`${showFilters ? 'grid' : 'hidden'} mt-3 gap-3 md:grid md:grid-cols-3`}>
            <div><label htmlFor="chore-status">Status</label><select id="chore-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="active">Active Chores</option><option value="inactive">Inactive Chores</option><option value="all">All Chores</option></select></div>
            <div><label htmlFor="chore-source">Chore Type</label><select id="chore-source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}><option value="all">All Chores</option><option value="parent">Parent-created</option><option value="child">Kid-created</option></select></div>
            <div><label htmlFor="chore-child">Child</label><select id="chore-child" value={childFilter} onChange={(e) => setChildFilter(e.target.value)}><option value="all">All Kids</option>{children.map((child) => <option key={child.id} value={child.id}>{child.nickname || child.full_name}</option>)}</select></div>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-slate-300">
            <span role="status">{filteredChores.length} of {chores.length} chores shown</span>
            {filtersChanged && <button type="button" onClick={resetFilters} className="rounded px-2 py-1 font-bold text-teal-800 underline focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-teal-200">Clear filters</button>}
          </div>
        </Card>

        {filteredChores.length === 0 ? <Card className="p-6 text-center"><p>{chores.length === 0 ? 'No chores yet. Add your first chore to get started.' : 'No chores match these filters.'}</p>{filtersChanged && <Button className="mt-3" variant="secondary" onClick={resetFilters}>Clear filters</Button>}</Card> :
          <Card>
            <div className="hidden grid-cols-[minmax(0,1fr)_9rem_5rem_7rem] items-center gap-3 border-b border-gray-200 px-4 py-2 text-xs font-extrabold uppercase tracking-wide text-gray-600 dark:border-slate-700 dark:text-slate-300 md:grid" aria-hidden="true"><span>Chore</span><span>Assigned to</span><span className="text-right">Reward</span><span className="text-right">Actions</span></div>
            <ul className="divide-y divide-gray-200 dark:divide-slate-700">
              {filteredChores.map((chore) => {
                const assigned = childById.get(chore.assigned_child_id);
                const assignment = chore.assigned_child_id ? assigned?.nickname || assigned?.full_name || 'Previously assigned child' : 'All Kids';
                return <li key={chore.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-3 py-3 transition-colors hover:bg-teal-50/70 dark:hover:bg-teal-950/25 md:grid-cols-[minmax(0,1fr)_9rem_5rem_7rem] md:px-4">
                  <div className="min-w-0">
                    <h3 className="break-words text-sm font-extrabold leading-snug">{chore.title}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${getSourceClass(chore)}`}>{getSourceLabel(chore)}</span>{!chore.is_active && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-700 dark:bg-slate-700 dark:text-slate-200">Inactive</span>}</div>
                  </div>
                  <span className="col-start-1 row-start-2 break-words text-xs text-gray-600 dark:text-slate-300 md:col-auto md:row-auto md:text-sm"><span className="md:sr-only">Assigned to: </span>{assignment}</span>
                  <span className="col-start-2 row-start-1 whitespace-nowrap text-right text-base font-extrabold text-teal-800 dark:text-teal-200 md:col-auto md:row-auto"><span className="sr-only">Reward: </span>{formatCurrency(chore.value_cents)}</span>
                  <div className="col-start-2 row-start-2 md:col-auto md:row-auto"><ChoreActions chore={chore} disabled={busy || isWriteLocked} onEdit={() => startEditing(chore)} onToggle={() => chore.is_active ? deactivate(chore) : reactivate(chore)} /></div>
                </li>;
              })}
            </ul>
          </Card>
        }
      </div>}

      {showCreateForm && <ChoreDialog title="Add Chore" busy={busy} onClose={closeCreate}>
        <p className="mb-4 text-sm text-gray-600 dark:text-slate-300">Create a chore for all kids or assign it to one child.</p>
        <form onSubmit={handleCreate}>
          <fieldset disabled={isWriteLocked || busy}><ChoreFields value={form} onChange={setForm} childrenList={children} prefix="create" /></fieldset>
          {error && <p role="alert" className="mt-3 text-sm font-semibold text-red-700 dark:text-red-300">{error}</p>}
          <div className="mt-5 flex justify-end gap-2"><Button type="button" variant="secondary" disabled={busy} onClick={closeCreate}>Cancel</Button><Button type="submit" disabled={isWriteLocked || busy}>{saving ? 'Saving…' : 'Add Chore'}</Button></div>
        </form>
      </ChoreDialog>}
      {editingChore && <ChoreDialog title="Edit Chore" busy={busy} onClose={closeEdit}>
        <form onSubmit={(e) => { e.preventDefault(); saveEdit(editingChore); }}>
          <fieldset disabled={isWriteLocked || busy}><ChoreFields value={editForm} onChange={setEditForm} childrenList={children} prefix="edit" /></fieldset>
          {error && <p role="alert" className="mt-3 text-sm font-semibold text-red-700 dark:text-red-300">{error}</p>}
          <div className="mt-5 flex justify-end gap-2"><Button type="button" variant="secondary" disabled={busy} onClick={closeEdit}>Cancel</Button><Button type="submit" disabled={isWriteLocked || busy}>{busy ? 'Saving…' : 'Save Changes'}</Button></div>
        </form>
      </ChoreDialog>}
    </AppLayout>
  );
};

export default ManageChores;
